import { FC, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState, IconButton, Input, Notice } from '../../ui';
import BundleCard from './BundleCard';
import ItemCard from './ItemCard';
import './ItemSelector.css';

type ItemSelectorProps = {
  categoryItems: CategoryItem[];
  bundles: CatalogBundle[];
  /** True once the live catalogue has answered; see App. */
  catalogLoaded: boolean;
  /** True once that fetch has failed; with no cache the till has nothing to sell. */
  catalogFailed: boolean;
  /** True at phone widths; decided once in Cashier so the whole till agrees. */
  compact: boolean;
};

const readCachedItems = (): CategoryItem[] => {
  try {
    const cachedData = localStorage.getItem('cachedData');
    if (!cachedData) return [];
    const parsed: unknown = JSON.parse(cachedData);
    // A truncated write or a hand-edited value must not take the till down on
    // startup: fall back to an empty menu and let the live catalogue replace it.
    // The cart restore reads its stored order the same way.
    return Array.isArray(parsed) ? (parsed as CategoryItem[]) : [];
  } catch {
    return [];
  }
};

/** The scroll target of one category section. */
const sectionId = (index: number) => 'tillCategory-' + index;

/** Spec 41: a compact search action, hidden until asked for. */
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M10 4a6 6 0 1 0 3.6 10.8l4.3 4.3 1.4-1.4-4.3-4.3A6 6 0 0 0 10 4zm0 2a4 4 0 1 1 0 8 4 4 0 0 1 0-8z" fill="currentColor" />
  </svg>
);

const ItemSelector: FC<ItemSelectorProps> = ({ categoryItems, bundles, catalogLoaded, catalogFailed, compact }) => {
  const [cachedItems] = useState<CategoryItem[]>(readCachedItems);
  const menuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  /** The section the sticky chips highlight; null is "All" (spec 8). */
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  /** Spec 40/41: the phone search action, closed until the operator asks for it. */
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** The browsing offset to restore when a search is cleared (spec 42/47). */
  const savedScroll = useRef(0);
  const wasSearching = useRef(false);

  // Render the live menu once it has answered, even when that answer is empty
  // (every item hidden); only an unanswered or failed fetch falls back to the
  // cached copy. Bundles have no local fallback here: App seeds them from the
  // offline cache.
  const selectableItems = catalogLoaded ? categoryItems : cachedItems;

  /*
   * A cold start with no cache must say what is happening instead of rendering
   * an empty menu, which reads exactly like an empty catalogue. A warm cache
   * renders immediately and never flashes the cue; a catalogue that genuinely
   * answered empty still renders nothing, as before.
   */
  const nothingToSell = selectableItems.length === 0 && bundles.length === 0;
  const loading = nothingToSell && !catalogLoaded && !catalogFailed;
  const unavailable = nothingToSell && !catalogLoaded && catalogFailed;

  // Bundle tiles show "N from X" using the catalogue the till already has.
  const itemsById = useMemo(() => {
    const map = new Map<number, Item>();
    for (const entry of selectableItems) {
      for (const item of entry.items) map.set(item.id, item);
    }
    return map;
  }, [selectableItems]);

  const categoryByItemId = useMemo(() => {
    const map = new Map<number, string>();
    for (const entry of selectableItems) {
      for (const item of entry.items) map.set(item.id, entry.category);
    }
    return map;
  }, [selectableItems]);

  useEffect(() => {
    // Cache the latest items so they can be shown before the sheet finishes loading
    if (categoryItems.length > 0) {
      localStorage.setItem('cachedData', JSON.stringify(categoryItems));
    }
  }, [categoryItems]);

  /** The categories in menu order, with the stable index the jump targets use. */
  const categories = useMemo(() => selectableItems.map((entry, index) => ({ ...entry, index })), [selectableItems]);

  const searching = query.trim().length > 0;

  /** Spec 40/42: every product whose name (or category) matches, as one flat grid. */
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return selectableItems.flatMap((entry) =>
      entry.items
        .filter((item) => item.title.toLowerCase().includes(needle) || entry.category.toLowerCase().includes(needle))
        .map((item) => ({ item, palette2: entry.palette2, palette3: entry.palette3 }))
    );
  }, [selectableItems, query]);

  /*
   * Spec 8: the highlighted chip follows the scroll. An IntersectionObserver is
   * the trigger; the section is chosen by where the sticky bar's bottom edge sits,
   * so no scroll handler runs per frame.
   */
  useEffect(() => {
    if (!compact || searching) return undefined;
    const menu = menuRef.current;
    if (!menu) return undefined;
    const sections = Array.from(menu.querySelectorAll<HTMLElement>('.itemCategory[data-category-index]'));
    if (sections.length === 0) return undefined;

    const pick = () => {
      if (menu.scrollTop <= 4) {
        setActiveCategory((current) => (current === null ? current : null));
        return;
      }
      // The last section is often shorter than the container, so it can never
      // reach the top line; at the end of the list it is the one being viewed.
      if (menu.scrollTop + menu.clientHeight >= menu.scrollHeight - 2) {
        const last = Number(sections[sections.length - 1].dataset.categoryIndex);
        setActiveCategory((current) => (current === last ? current : last));
        return;
      }
      const navHeight = navRef.current?.offsetHeight ?? 0;
      const line = menu.getBoundingClientRect().top + navHeight + 8;
      let next: number | null = null;
      for (const section of sections) {
        if (section.getBoundingClientRect().top <= line + 1) {
          next = Number(section.dataset.categoryIndex);
        }
      }
      if (next !== null) setActiveCategory((current) => (current === next ? current : next));
    };

    const observer = new IntersectionObserver(pick, { root: menu, rootMargin: '-56px 0px -55% 0px', threshold: 0 });
    for (const section of sections) observer.observe(section);
    pick();
    return () => observer.disconnect();
  }, [compact, searching, categories.length]);

  // Spec 42/47: clearing a search returns to the browsing place it left, not the
  // top of the catalog.
  useEffect(() => {
    if (!searching && wasSearching.current && menuRef.current) {
      menuRef.current.scrollTop = savedScroll.current;
    }
    wasSearching.current = searching;
  }, [searching]);

  const onQueryChange = (value: string) => {
    if (!query && value && menuRef.current) savedScroll.current = menuRef.current.scrollTop;
    setQuery(value);
  };

  const toggleSearch = () => {
    if (searchOpen) {
      setQuery('');
      setSearchOpen(false);
      return;
    }
    setSearchOpen(true);
  };

  /** Spec 7/9: jump the scroll container so the heading lands just under the
      sticky header + category row, never underneath it. */
  const jumpToCategory = (index: number) => {
    const menu = menuRef.current;
    const target = document.getElementById(sectionId(index));
    if (!menu || !target) return;
    const offset = (navRef.current?.offsetHeight ?? 0) + 8;
    const top = target.getBoundingClientRect().top - menu.getBoundingClientRect().top + menu.scrollTop - offset;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    menu.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
    setActiveCategory(index);
  };

  const jumpToTop = () => {
    const menu = menuRef.current;
    if (menu) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      menu.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    setActiveCategory(null);
  };

  /** Desktop keeps the anchor jump: a no-JS fallback and the shipped behaviour. */
  const jumpToAnchor = (event: MouseEvent<HTMLAnchorElement>, index: number) => {
    event.preventDefault();
    jumpToCategory(index);
  };

  return (
    <div className="itemSelector" ref={menuRef}>
      {loading && <Notice variant="info">Loading your menu…</Notice>}
      {unavailable && <EmptyState title="No menu on this device" description="Connect to the internet to load your items." />}

      {!nothingToSell &&
        (compact ? (
          <>
            {/* Spec 6-9: a sticky horizontal category row that JUMPS to a section
                (all sections stay in the DOM) and follows the scroll; the search
                action is hidden until asked for (spec 41). */}
            <div className="menuNav" ref={navRef}>
              <div className="categoryChips" role="group" aria-label="Menu categories">
                <button
                  type="button"
                  className="categoryChip"
                  aria-current={activeCategory === null ? 'true' : undefined}
                  onClick={jumpToTop}
                >
                  All
                </button>
                {categories.map((entry) => (
                  <button
                    key={entry.index}
                    type="button"
                    className="categoryChip"
                    aria-current={activeCategory === entry.index ? 'true' : undefined}
                    onClick={() => jumpToCategory(entry.index)}
                  >
                    {entry.category}
                  </button>
                ))}
              </div>
              <IconButton
                label={searchOpen ? 'Close search' : 'Search products'}
                className="categorySearchToggle"
                aria-expanded={searchOpen}
                onClick={toggleSearch}
              >
                <SearchIcon />
              </IconButton>
            </div>

            {searchOpen && (
              <div className="itemSearch">
                <Input
                  type="search"
                  className="itemSearchInput"
                  placeholder="Search products…"
                  aria-label="Search products"
                  value={query}
                  onChange={(event) => onQueryChange(event.target.value)}
                  autoFocus
                />
              </div>
            )}

            {searching ? (
              /* Spec 42: matches as one compact grid, with no category sections. */
              <div className="itemCategory itemSearchResults">
                {matches.length > 0 ? (
                  <div className="itemsGrid">
                    {matches.map(({ item, palette2, palette3 }) => (
                      <ItemCard item={item} key={item.id} bg={palette2} fg={palette3} compact />
                    ))}
                  </div>
                ) : (
                  <EmptyState title="No products match" description="Try a different word, or clear the search." />
                )}
              </div>
            ) : (
              <>
                {/* Spec 19-22: ONE horizontal bundle row before the catalog. */}
                {bundles.length > 0 && (
                  <div className="bundleSection">
                    <h2 className="categoryTitle bundleTitle">bundles</h2>
                    <div className="bundleRail">
                      {bundles.map((bundle) => (
                        <BundleCard bundle={bundle} key={bundle.id} itemsById={itemsById} categoryByItemId={categoryByItemId} rail />
                      ))}
                    </div>
                  </div>
                )}
                {categories.map((entry) => (
                  <div key={entry.index} id={sectionId(entry.index)} data-category-index={entry.index} className="itemCategory">
                    {/* The heading is painted on the app surface, which flips with
                        the theme, so it takes a theme role; the category's palette3
                        stays as a swatch so the identity survives. */}
                    <h2 className="categoryTitle">
                      <span className="categorySwatch" style={{ backgroundColor: entry.palette3 }} aria-hidden="true" />
                      {entry.category}
                    </h2>
                    <div className="itemsGrid">
                      {entry.items.map((item, idx) => (
                        <ItemCard item={item} key={idx} bg={entry.palette2} fg={entry.palette3} compact />
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        ) : (
          <>
            {/* Desktop is unchanged (spec 53): the jump row, one bundle section,
                then the category sections. */}
            <nav className="itemJumps" aria-label="Menu categories" ref={navRef}>
              {categories.map((entry) => (
                <a
                  key={entry.index}
                  className="itemJump"
                  href={'#' + sectionId(entry.index)}
                  onClick={(event) => jumpToAnchor(event, entry.index)}
                >
                  {entry.category}
                </a>
              ))}
            </nav>
            {bundles.length > 0 && (
              <div className="bundleSection">
                <h2 className="categoryTitle bundleTitle">bundles</h2>
                <div className="itemsGrid">
                  {bundles.map((bundle) => (
                    <BundleCard bundle={bundle} key={bundle.id} itemsById={itemsById} categoryByItemId={categoryByItemId} />
                  ))}
                </div>
              </div>
            )}
            {categories.map((entry) => (
              <div key={entry.index} id={sectionId(entry.index)} className="itemCategory">
                <h2 className="categoryTitle">
                  <span className="categorySwatch" style={{ backgroundColor: entry.palette3 }} aria-hidden="true" />
                  {entry.category}
                </h2>
                <div className="itemsGrid">
                  {entry.items.map((item, idx) => (
                    <ItemCard item={item} key={idx} bg={entry.palette2} fg={entry.palette3} />
                  ))}
                </div>
              </div>
            ))}
          </>
        ))}
    </div>
  );
};

export default ItemSelector;
